import {makeAutoObservable, runInAction} from 'mobx';
import {makeLoggable} from 'mobx-log';

import config from '../config';
import {AirtablePromotionItem} from '../models/airtable';
import {AppRoute} from '../models/AppRoute';
import apiService from '../transport/apiService';
import {isGiftCardsPromotion} from '../utils/giftCards';

import type {RootStore} from './rootStore';

const CAROUSEL = 'Carousel';
const FEED = 'Feed';

export const isDrawnWinnersPromotion = (promotion: AirtablePromotionItem) =>
  promotion.status === 'Drawn Winners';
export const isEcommerceOnlyPromotion = (promotion: AirtablePromotionItem) =>
  promotion.eCommerceOnly;
export const isInStoreOnlyPromotion = (promotion: AirtablePromotionItem) =>
  promotion.inStoreEntry;
export const isMembersOnlyPromotion = (promotion: AirtablePromotionItem) =>
  promotion.membersOnly;
export const createFilterMembersOnlyPromotion = (rootStore: RootStore) => (promotion: AirtablePromotionItem) => {
  const isSelectedStoreLoyaltyEnabled = rootStore.shopSelectionStore.loyaltyEnabled;
  const isLoggedIn = rootStore.userStore.isLoggedIn();
  return isMembersOnlyPromotion(promotion) ? isSelectedStoreLoyaltyEnabled && isLoggedIn : true;
}

const isActivePromotion = (promotion: AirtablePromotionItem) =>
  !isDrawnWinnersPromotion(promotion);
const isActiveInStorePromotion = (promotion: AirtablePromotionItem) =>
  !isDrawnWinnersPromotion(promotion) && !isEcommerceOnlyPromotion(promotion);
const isDeepLinkOnlyPromotion = (promotion: AirtablePromotionItem) =>
  promotion.hideOffer;
const isListedEcommerceOnlyPromotion = (promotion: AirtablePromotionItem) =>
  isEcommerceOnlyPromotion(promotion) && !isDeepLinkOnlyPromotion(promotion);

const isCarouselPromotion = (promotion: AirtablePromotionItem) => {
  if (isDrawnWinnersPromotion(promotion) && !promotion.heroImage) {
    return false;
  }
  if (config.FF_LOYALTY_ENABLED) {
    return [CAROUSEL, FEED].includes(promotion.type);
  } else {
    return promotion.type === CAROUSEL;
  }
};
const isFeedPromotion = (item: AirtablePromotionItem) =>
  item.type === FEED && !isDeepLinkOnlyPromotion(item);

export class PromotionsStore {
  rootStore: RootStore;

  carouselData: AirtablePromotionItem[] = [];
  feedData: AirtablePromotionItem[] = [];

  activePromotions: AirtablePromotionItem[] = [];
  rawPromotions: AirtablePromotionItem[] = [];
  promotions: AirtablePromotionItem[] = [];
  drawnWinnersPromotions: AirtablePromotionItem[] = [];

  // Some promotions are not listed (visible) in the "feed" or "promotions"
  // screens. Those are mean to be reachable only through deep links.
  deepLinkOnlyPromotions: AirtablePromotionItem[] = [];
  listedECommerceOnlyPromotions: AirtablePromotionItem[] = [];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
    makeLoggable(this);
  }

  getPromotions = async (storeNumber: any, storeState: any) => {
    const filterMembersOnlyPromotion = createFilterMembersOnlyPromotion(this.rootStore);
    try {
      let response: AirtablePromotionItem[] = await apiService.getPromotions(
        storeNumber,
        storeState,
      );
      // TMP: 4048-Version with Gift Cards toggled off-------------------------
      if (config.FF_IS_GIFT_CARDS_ENABLED === false) {
        response = response.filter(
          promotion => !isGiftCardsPromotion(promotion),
        );
      }
      //-----------------------------------------------------------------------
      runInAction(() => {
        this.rawPromotions = response;
        const basePromotions = response.filter(filterMembersOnlyPromotion);
        const activeInStorePromotion = basePromotions.filter(isActiveInStorePromotion);
        this.activePromotions = basePromotions.filter(isActivePromotion);
        this.promotions = activeInStorePromotion;
        this.carouselData = activeInStorePromotion.filter(isCarouselPromotion);
        this.feedData = activeInStorePromotion.filter(isFeedPromotion);

        this.drawnWinnersPromotions = response.filter(isDrawnWinnersPromotion);
        this.listedECommerceOnlyPromotions = response.filter(
          isListedEcommerceOnlyPromotion,
        );
        this.deepLinkOnlyPromotions = response.filter(isDeepLinkOnlyPromotion);
      });
    } catch (err) {
      console.log('activations err', err);
    }
  };

  // We don't store the linked products in this store as a promotion can be
  // viewed in multiple places, so each promotion screen keeps its own list
  // of linked products.
  loadLinkedProducts = (promotion?: AirtablePromotionItem) => {
    if (!promotion?.barcodes?.length) {
      return [];
    }

    return apiService
      .searchByBarcode(promotion.barcodes)
      .then(data => data)
      .catch(err => {
        console.log('err loading promotion linked products', err);
        return [];
      });
  };

  // Util

  // Ugly
  navigateToGiftCardsPromotionOrLoadingScreen = (
    navigate: Function,
    fromRoot = false,
  ) => {
    const giftCardsPromotion = this.rawPromotions?.find(isGiftCardsPromotion);
    if (giftCardsPromotion) {
      if (fromRoot) {
        navigate(AppRoute.ROOT, {
          screen: AppRoute.MAIN,
          params: {
            screen: AppRoute.PROMOTION,
            params: {
              promotion: giftCardsPromotion,
            },
          },
        });
      } else {
        navigate(AppRoute.PROMOTION, {
          promotion: giftCardsPromotion,
        });
      }
    } else {
      if (fromRoot) {
        navigate(AppRoute.ROOT, {
          screen: AppRoute.MAIN,
          params: {
            screen: AppRoute.GIFT_CARDS_PROMOTION_WAITER,
          },
        });
      } else {
        navigate(AppRoute.GIFT_CARDS_PROMOTION_WAITER);
      }
    }
  };
}
